import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, Variants, AnimatePresence } from "framer-motion";
import {
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineCreditCard,
  HiOutlineTicket,
  HiOutlineUser,
  HiOutlineCalendar,
  HiOutlineBanknotes,
  HiOutlineReceiptPercent,
  HiOutlineDocumentText,
  HiOutlineXMark,
  HiOutlineSparkles,
} from "react-icons/hi2";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import HeaderBox from "components/navbar/HeaderBox";
import { useGetTransactionsQuery } from "reduxFiles/api/assessmentApi";
import { TransactionDTO, TransactionStatus } from "types/assessmentTypes";
import Pagination from "components/table/Pagination";
import { useScope } from "hooks/useScope";
import moment from "moment-jalaali";
import { Field } from "components/dynamic/DynamicForm";
import { enumToOptions } from "services/helper";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import DynamicFilter from "components/filter/DynamicFilter";
import { FieldValues } from "react-hook-form";
import { useLazyGetOrganizationsQuery } from "reduxFiles/api/companyApi";
import { useLazyGetUsersQuery } from "reduxFiles/api/userApi";

const STATUS_CONFIG = {
  SUCCESS: {
    topBar: "bg-gradient-to-r from-green-500 via-green-400 to-green-500",
    badgeBg: "bg-green-50 dark:bg-green-500/20",
    badgeText: "text-green-600 dark:text-green-300",
    icon: <HiOutlineCheckCircle className="h-3.5 w-3.5 text-green-700" />,
    label: "success",
    glow: "shadow-green-500/20",
    ring: "ring-1 ring-green-500/20",
    border: "hover:border-green-200 dark:hover:border-green-500/30",
    shadow: "shadow-lg shadow-green-100/50 dark:shadow-green-500/10",
    bg: "bg-white dark:bg-navy-800",
  },
  FAILED: {
    topBar: "bg-gradient-to-r from-red-500 via-red-400 to-rose-500",
    badgeBg: "bg-red-50 dark:bg-red-500/20",
    badgeText: "text-red-600 dark:text-red-300",
    icon: <HiOutlineXCircle className="h-3.5 w-3.5 text-red-700" />,
    label: "failed",
    glow: "shadow-red-500/20",
    ring: "ring-1 ring-red-500/20",
    border: "hover:border-red-200 dark:hover:border-red-500/30",
    shadow: "shadow-lg shadow-red-100/50 dark:shadow-red-500/10",
    bg: "bg-white dark:bg-navy-800",
  },
  PENDING: {
    topBar: "bg-gradient-to-r from-amber-500 via-amber-400 to-orange-500",
    badgeBg: "bg-amber-50 dark:bg-amber-500/20",
    badgeText: "text-amber-600 dark:text-amber-300",
    icon: <HiOutlineClock className="h-3.5 w-3.5 text-amber-700" />,
    label: "pending",
    glow: "shadow-amber-500/20",
    ring: "ring-1 ring-amber-500/20",
    border: "hover:border-amber-200 dark:hover:border-amber-500/30",
    shadow: "shadow-lg shadow-amber-100/50 dark:shadow-amber-500/10",
    bg: "bg-white dark:bg-navy-800",
  },
};

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 350,
      mass: 0.8,
    },
  },
  hover: {
    y: -6,
    scale: 1.01,
    transition: {
      type: "spring",
      damping: 15,
      stiffness: 400,
    },
  },
};

const TransactionsPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [page, setPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionDTO | null>(null);
  const scope = useScope();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // users
  const [triggerUsers] = useLazyGetUsersQuery();
  const searchUsers = async (text: string) => {
    try {
      const res = await triggerUsers({
        page: 1,
        limit: 50,
        filters: { search: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.fullname + " " + u.mobile || "-",
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // search organizations
  const [triggerOrganizations] = useLazyGetOrganizationsQuery();
  const searchOrganizations = async (text: string) => {
    try {
      const res = await triggerOrganizations({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const fields: Field[] = [
    {
      name: "userId",
      label: t("licenses.userId"),
      type: "select",
      onSearch: searchUsers,
      filtered: scope === "admin",
    },
    {
      name: "organizationId",
      label: t("organization.name"),
      type: "select",
      onSearch: searchOrganizations,
      filtered: scope === "admin",
    },
    {
      name: "refId",
      label: t("transactions.refId"),
      type: "text",
      filtered: true,
    },
    {
      name: "amount",
      label: t("transactions.amount"),
      type: "text",
      filtered: true,
    },
    {
      name: "status",
      label: t("transactions.status"),
      type: "select",
      options: enumToOptions(TransactionStatus, t, "transactions.statuses"),
      filtered: true,
    },
  ];

  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPage(1);
  };

  // api
  const {
    data: transactionsData,
    isLoading,
    isFetching,
    refetch,
  } = useGetTransactionsQuery({ scope, page, limit: 15, filters });

  const transactions = transactionsData?.items || [];
  const totalPages = transactionsData?.pageCount || 0;

  const formatPrice = (price: number) => price.toLocaleString("fa-IR");

  const formatDateTime = (date: string) => {
    return moment(date).format(
      lang === "fa" ? "jYYYY/jMM/jDD - HH:mm" : "YYYY/MM/DD - HH:mm"
    );
  };

  const getStatusBadge = (status: TransactionDTO["status"]) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
    return {
      ...config,
      label: t(`transactions.${config.label}`),
    };
  };

  const getGatewayName = (gateway: string) => t(`licenses.gateways.${gateway}`);

  const getUserDisplayName = (transaction: TransactionDTO) => {
    return transaction.user?.fullname || t("transactions.user");
  };

  const getUserSubText = (transaction: TransactionDTO) => {
    if (transaction.organizationId && transaction.organization?.name) {
      return transaction?.organization.name;
    }
    return t("transactions.customer");
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="loader w-6 text-blue-700" />
      </div>
    );
  }

  return (
    <div className="dark:to-navy-950 from-slate-50 min-h-full bg-gradient-to-br via-gray-50 to-blue-50 dark:from-navy-900 dark:via-navy-900">
      <HeaderBox
        title={t("transactions.myTransactions")}
        refreshBtn={refetch}
      />

      <MobileFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters((prev) => !prev)}
        onSubmit={handleFilterSubmit}
        fields={fields?.filter((f) => f.filtered)}
        loading={isFetching}
      />
      <DynamicFilter
        onSubmit={handleFilterSubmit}
        fields={fields?.filter((f) => f.filtered)}
        defaultValues={{}}
        loading={isFetching}
      />

      <div className="p-5 lg:p-7">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <div>
              <motion.h2
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-transparent bg-gradient-to-r text-xl font-bold text-gray-900 dark:text-gray-300"
              >
                {t("transactions.transactionsList")}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 }}
                className="text-xs text-gray-600 dark:text-gray-400"
              >
                {t("transactions.paginationInfo")}
              </motion.p>
            </div>
            {transactions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 rounded-xl bg-white/80 px-3 py-1.5 shadow-lg backdrop-blur-sm dark:bg-navy-800/80"
              >
                <div className="rounded-full bg-gradient-to-r from-brand-500 to-brand-600 p-1">
                  <HiOutlineReceiptPercent className="h-3 w-3 text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  {transactionsData.total} {t("transactions.transactions")}
                </span>
              </motion.div>
            )}
          </div>

          {transactions.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              className="relative overflow-hidden rounded-2xl bg-white/80 p-12 text-center shadow-xl backdrop-blur-sm dark:bg-navy-800/80"
            >
              <div className="absolute -right-20 -top-20 h-60 w-60 rounded-full bg-gradient-to-r from-brand-400/20 to-blue-400/20 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-gradient-to-r from-purple-400/20 to-pink-400/20 blur-3xl" />
              <div className="relative">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{
                    repeat: Infinity,
                    duration: 3,
                    ease: "easeInOut",
                  }}
                  className="mb-4 inline-flex rounded-full bg-gradient-to-br from-gray-100 to-gray-200 p-5 shadow-lg dark:from-gray-800 dark:to-gray-700"
                >
                  <HiOutlineBanknotes className="h-12 w-12 text-gray-400" />
                </motion.div>
                <h3 className="mb-2 text-xl font-bold text-gray-800 dark:text-white">
                  {t("transactions.noTransactions")}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("transactions.noTransactionsDesc")}
                </p>
              </div>
            </motion.div>
          ) : (
            <>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
              >
                {transactions.map((transaction) => {
                  const statusBadge = getStatusBadge(transaction.status);
                  const isOrganization = !!transaction.organizationId;
                  const refId = transaction.refId || "-";
                  const userName = getUserDisplayName(transaction);
                  const userSubText = getUserSubText(transaction);
                  const hasLicenses = transaction.licenses.length > 0;

                  return (
                    <motion.div
                      key={transaction.id}
                      variants={cardVariants}
                      whileHover="hover"
                      className={`group relative overflow-hidden rounded-2xl ${statusBadge.bg} shadow-xl transition-all duration-300 ${statusBadge.border} ${statusBadge.glow} ${statusBadge.ring} ${statusBadge.shadow}`}
                    >
                      <div
                        className={`absolute left-0 right-0 top-0 h-1.5 ${statusBadge.topBar}`}
                      />

                      {/* header card */}
                      <div className="relative flex flex-wrap items-center justify-between border-b border-gray-100/80 px-4 py-3.5 dark:border-gray-700/50">
                        <div className="flex items-center gap-3">
                          <motion.div
                            whileHover={{ scale: 1.05, rotate: 3 }}
                            className="rounded-full bg-gradient-to-br from-gray-100 to-gray-200 p-2 shadow-inner dark:from-gray-700 dark:to-gray-800"
                          >
                            {isOrganization ? (
                              <HiOutlineOfficeBuilding className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            ) : (
                              <HiOutlineUser className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            )}
                          </motion.div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                              {userName?.slice(0, 22) || t("transactions.user")}
                            </p>
                            <p className="text-xs text-gray-700 dark:text-gray-400">
                              {userSubText}
                            </p>
                          </div>
                        </div>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          className={`flex items-center gap-1.5 rounded-full px-3 py-1 ${statusBadge.badgeBg} backdrop-blur-sm`}
                        >
                          {statusBadge.icon}
                          <span
                            className={`text-xs font-bold ${statusBadge.badgeText}`}
                          >
                            {statusBadge.label}
                          </span>
                        </motion.div>
                      </div>

                      {/* content */}
                      <div className="relative p-4">
                        {/* price and code */}
                        <div className="mb-3.5 flex items-center justify-between">
                          <div className="flex items-center gap-1.5 rounded-lg bg-gray-50/80 px-2.5 py-1.5 dark:bg-gray-800/50">
                            <HiOutlineDocumentText className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-mono text-xs font-bold text-gray-700 dark:text-gray-300">
                              {refId}
                            </span>
                          </div>
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="text-left"
                          >
                            <span className="text-transparent bg-gradient-to-r from-brand-600 to-brand-500 bg-clip-text text-2xl font-black dark:from-brand-400 dark:to-brand-300 dark:text-gray-300">
                              {formatPrice(transaction.amount)}
                            </span>
                            <span className="mr-0.5 text-[10px] font-medium text-gray-400">
                              {t("transactions.priceInit")}
                            </span>
                          </motion.div>
                        </div>

                        {/* detail */}
                        <div className="mb-3.5 grid grid-cols-2 gap-2">
                          <motion.div
                            whileHover={{ scale: 1.01, x: 1 }}
                            className="flex items-center gap-2 rounded-lg bg-gray-50/80 px-2.5 py-2 transition-all dark:bg-gray-800/50"
                          >
                            <div className="rounded-md bg-brand-100 p-1 dark:bg-brand-500/20">
                              <HiOutlineCalendar className="h-3 w-3 text-brand-600 dark:text-brand-400" />
                            </div>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {formatDateTime(transaction.createdAt)}
                            </span>
                          </motion.div>
                          <motion.div
                            whileHover={{ scale: 1.01, x: 1 }}
                            className="flex items-center gap-2 rounded-lg bg-gray-50/80 px-2.5 py-2 transition-all dark:bg-gray-800/50"
                          >
                            <div className="rounded-md bg-purple-100 p-1 dark:bg-purple-500/20">
                              <HiOutlineTicket className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                            </div>
                            <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                              {transaction.quantity} {t("transactions.license")}
                            </span>
                          </motion.div>
                          <motion.div
                            whileHover={{ scale: 1.01, x: 1 }}
                            className="flex items-center gap-2 rounded-lg bg-gray-50/80 px-2.5 py-2 transition-all dark:bg-gray-800/50"
                          >
                            <div className="rounded-md bg-blue-100 p-1 dark:bg-blue-500/20">
                              <HiOutlineCreditCard className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                            </div>
                            <span className="font-mono text-xs text-gray-600 dark:text-gray-400">
                              #{transaction.id}
                            </span>
                          </motion.div>
                          <motion.div
                            whileHover={{ scale: 1.01, x: 1 }}
                            className="flex items-center gap-2 rounded-lg bg-gray-50/80 px-2.5 py-2 transition-all dark:bg-gray-800/50"
                          >
                            <div className="rounded-md bg-orange-100 p-1 dark:bg-orange-500/20">
                              <HiOutlineSparkles className="h-3 w-3 text-orange-600 dark:text-orange-400" />
                            </div>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {getGatewayName(transaction.gateway)}
                            </span>
                          </motion.div>
                        </div>

                        {/* buttons */}
                        {hasLicenses && (
                          <motion.button
                            onClick={() => setSelectedTransaction(transaction)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-500/10 to-brand-600/10 px-3.5 py-2.5 transition-all hover:from-brand-500/20 hover:to-brand-600/20 dark:from-brand-500/20 dark:to-brand-600/20"
                          >
                            <HiOutlineTicket className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                            <span className="text-xs font-medium text-brand-600 dark:text-brand-200">
                              {t("transactions.viewLicenses")}
                            </span>
                            <span className="rounded-full bg-brand-500/20 px-2 py-0.5 text-[10px] font-medium text-brand-600 dark:bg-brand-500/30 dark:text-brand-100">
                              {transaction.licenses.length}
                            </span>
                          </motion.button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>

              {totalPages > 1 && (
                <div className="mt-8 flex justify-center">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* modal licenses */}
      <AnimatePresence>
        {selectedTransaction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-black/50 dark:bg-black/70 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md"
            onClick={() => setSelectedTransaction(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800"
              onClick={(e) => e.stopPropagation()}
            >
              {/* modal header */}
              <div className="flex items-center justify-between border-b border-gray-100/80 px-6 py-4 dark:border-gray-700/50">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-gradient-to-br from-brand-100 to-brand-200 p-2 dark:from-brand-500/20 dark:to-brand-600/20">
                    <HiOutlineTicket className="h-5 w-5 text-brand-600 dark:text-brand-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                      {t("transactions.purchasedLicenses")}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("transactions.transactionId")}: #
                      {selectedTransaction.id}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedTransaction(null)}
                  className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                >
                  <HiOutlineXMark className="h-5 w-5" />
                </button>
              </div>

              {/* licenses */}
              <div className="custom-scrollbar max-h-96 overflow-y-auto p-6">
                <div className="flex flex-col gap-2">
                  {selectedTransaction.licenses.map((license, idx) => (
                    <motion.div
                      key={license.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-center justify-between rounded-xl bg-gray-50/80 px-4 py-3 transition-all hover:bg-gray-100/80 dark:bg-gray-700/50 dark:hover:bg-gray-700/80"
                    >
                      <span className="font-mono text-sm font-semibold tracking-wider text-gray-700 dark:text-gray-300">
                        {license.licenseKey}
                      </span>
                      {selectedTransaction.status === "SUCCESS" && (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
                          <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                          {t("transactions.active")}
                        </span>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* more */}
                <div className="mt-4 rounded-xl bg-gray-50/80 p-4 dark:bg-gray-700/30">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t("transactions.totalLicenses")}
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white">
                      {selectedTransaction.licenses.length}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t("transactions.totalAmount")}
                    </span>
                    <span className="font-bold text-brand-600 dark:text-brand-400">
                      {formatPrice(selectedTransaction.amount)}{" "}
                      {t("transactions.priceInit")}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedTransaction(null)}
                  className="mt-4 w-full rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-2.5 text-sm font-medium text-white transition-all hover:from-brand-600 hover:to-brand-700"
                >
                  {t("buttons.close")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TransactionsPage;
